import type { IntlShape } from "react-intl";
import z, {
  array,
  date,
  literal,
  number,
  object,
  string,
  union,
  null as zodNull,
  ZodIssueCode,
  coerce,
} from "zod";

import { MIN_ADULT_AGE } from "@/utils/constants";
import { citySchema } from "@/utils/locations";

export const getDatesValidationSchema = (intl: IntlShape) =>
  object({
    checkIn: date({
      required_error: intl.formatMessage({
        id: "hotels.validation.dates.checkIn",
        defaultMessage: "Please select check In date",
      }),
    }),
    checkOut: date({
      required_error: intl.formatMessage({
        id: "hotels.validation.dates.checkOut",
        defaultMessage: "Please select check Out date",
      }),
    }),
  }).superRefine((values, ctx) => {
    const { checkIn, checkOut } = values;

    if (checkOut < checkIn) {
      ctx.addIssue({
        code: "custom",
        path: ["checkOut"],
        message: intl.formatMessage({
          id: "hotels.validation.dates.min",
          defaultMessage: "Check Out date cannot be set before Check In",
        }),
      });
    }
  });

export const getRoomsValidationSchema = (intl: IntlShape) =>
  array(
    object({
      adults: number().min(1),
      children: number().min(0),
      childrenAges: array(
        number()
          .int()
          .min(0)
          .max(MIN_ADULT_AGE - 1),
      ),
    }),
  )
    .min(
      1,
      intl.formatMessage({
        id: "hotels.validation.roomsMin",
        defaultMessage: "Choose at least 1 room",
      }),
    )
    .max(
      5,
      intl.formatMessage({
        id: "hotels.validation.roomsMax",
        defaultMessage: "Choose no more than 5 rooms",
      }),
    );

export const getHotelSearchFormInputSchema = (intl: IntlShape) =>
  object({
    destination: citySchema.nullable(),
    dates: object({
      checkIn: coerce.date(),
      checkOut: coerce.date(),
    }),
    rooms: getRoomsValidationSchema(intl),
  });
export type HotelSearchFormInputValues = z.infer<
  ReturnType<typeof getHotelSearchFormInputSchema>
>;

export const getHotelSearchFormSchema = (intl: IntlShape) =>
  object({
    destination: union([
      object({
        id: string().min(1).regex(/^\d+$/),
        name: string().min(
          1,
          intl.formatMessage({
            id: "hotels.validation.destination",
            defaultMessage: "Please select destination",
          }),
        ),
        code: string().optional(),
        countryName: string(),
        countryCode: string().optional(),
        type: literal("CITY"),
      }),
      zodNull(),
    ]).superRefine((v, ctx) => {
      if (v === null) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message: intl.formatMessage({
            id: "hotels.validation.destination",
            defaultMessage: "Please select destination",
          }),
        });
      }
    }),
    dates: getDatesValidationSchema(intl),
    rooms: getRoomsValidationSchema(intl),
  });

export type HotelSearchFormValues = z.infer<
  ReturnType<typeof getHotelSearchFormSchema>
>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const typecheck: z.input<ReturnType<typeof getHotelSearchFormSchema>> =
  {} as HotelSearchFormInputValues;
